import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CircleNotch, LockKey, CheckCircle } from "@phosphor-icons/react";
import { LogoMark, Wordmark } from "@/components/Logo";

export default function Login() {
  // There's no free trial and no self-serve sign-up: accounts are created only after a
  // paid Stripe subscription (see backend /auth/register + _provision_from_stripe_customer),
  // which emails the customer a set-password link. Old marketing links may still carry
  // ?mode=register from before that change — show a pointer to pricing instead of a
  // (now-disabled) sign-up form.
  const [searchParams] = useSearchParams();
  const cameFromRegisterLink = searchParams.get("mode") === "register";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [twoFA, setTwoFA] = useState(null); // temp_token
  const [code, setCode] = useState("");
  const { afterAuth } = useAuth();
  const nav = useNavigate();

  const submit = async () => {
    if (!email || !password) { toast.error("Email and password required"); return; }
    setLoading(true);
    try {
      const r = await api.login({ email, password });
      if (r.data.requires_2fa) { setTwoFA(r.data.temp_token); }
      else { await afterAuth(r.data.user); nav("/"); }
    } catch (e) {
      toast.error(apiError(e, "Authentication failed"));
    } finally { setLoading(false); }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const r = await api.verify2fa(twoFA, code);
      await afterAuth(r.data.user);
      nav("/");
    } catch (e) {
      toast.error(apiError(e, "Invalid code"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden" style={{ background: "var(--surface)", borderRight: "1px solid var(--line)" }}>
        <a href="https://jumpseo.ai" className="flex items-center gap-3 w-fit">
          <LogoMark size={40} />
          <div>
            <Wordmark className="text-lg text-white" />
            <div className="mono text-[10px] tracking-widest" style={{ color: "var(--accent)" }}>SEO ENGINE</div>
          </div>
        </a>
        <div>
          <h1 className="text-4xl font-bold leading-tight mb-4">Rank first.<br />Convert faster.</h1>
          <p className="text-sm max-w-md" style={{ color: "var(--text-2)" }}>
            SEO Automation and Link Builder for Smart Businesses.
            <br />
            <span className="mono text-xs" style={{ color: "var(--text)" }}>
              SEO Audits | AI Long Form Content | Console Rankings | AI Conversion Pipeline
            </span>
          </p>
          <ul className="space-y-2 mt-6">
            {[
              "Technical SEO audits with a score and clear fixes",
              "AI content, published and tuned to your business",
              "Real link building that earns domain reputation",
              "Global or hyperlocal SEO, built for your market",
              "Competitor intelligence to stay ahead",
              "Rank and lead tracking, on one panel",
              "Organic rankings in months, not years",
              "Save thousands versus an agency retainer",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--text)" }}>
                <CheckCircle size={17} weight="fill" color="#E30613" className="mt-0.5 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs" style={{ color: "var(--text-2)" }}>
            <a href="https://jumpseo.ai" className="hover:underline hover:text-white">Home</a>
            <a href="https://jumpseo.ai/how-it-works" className="hover:underline hover:text-white">How it works</a>
            <a href="https://jumpseo.ai/pricing" className="hover:underline hover:text-white">Pricing</a>
            <a href="https://jumpseo.ai/support" className="hover:underline hover:text-white">Support</a>
            <a href="https://jumpseo.ai/blog/" className="hover:underline hover:text-white">Blog</a>
            <a href="https://jumpseo.ai/about" className="hover:underline hover:text-white">About</a>
          </div>
          <div className="mono text-xs" style={{ color: "var(--text-2)" }}>jumpseo.ai · A CLOUDSILICON LIMITED project · Encrypted credentials</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <a href="https://jumpseo.ai" className="lg:hidden flex items-center gap-3 mb-8 w-fit">
            <LogoMark size={36} />
            <Wordmark className="text-lg text-white" />
          </a>

          {!twoFA ? (
            <>
              <h2 className="text-2xl font-bold mb-1">Sign in</h2>
              <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>
                Access your SEO projects
              </p>
              {cameFromRegisterLink && (
                <p className="text-sm mb-4 px-3 py-2.5 border" style={{ color: "var(--text-2)", borderColor: "var(--line)" }}>
                  There's no free sign-up — subscribe to a plan and we'll email you a link to set your
                  password.{" "}
                  <a href="https://jumpseo.ai/pricing" className="font-semibold hover:underline" style={{ color: "var(--accent)" }}>
                    See pricing →
                  </a>
                </p>
              )}
              <div className="space-y-3">
                <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full text-sm px-3 py-2.5 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
                <input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Password" className="w-full text-sm px-3 py-2.5 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
                <button data-testid="auth-submit" onClick={submit} disabled={loading} className="w-full px-4 py-3 text-sm font-bold flex items-center justify-center gap-2 text-white disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
                  {loading ? <CircleNotch size={16} className="animate-spin" /> : null}
                  Sign in
                </button>
              </div>
              <p className="text-sm mt-6 text-center" style={{ color: "var(--text-2)" }}>
                No account?{" "}
                <a href="https://jumpseo.ai/pricing" className="font-semibold hover:underline" style={{ color: "var(--accent)" }}>
                  See pricing
                </a>
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1"><LockKey size={22} weight="duotone" color="var(--accent)" /><h2 className="text-2xl font-bold">Two-factor</h2></div>
              <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>Enter the 6-digit code from your authenticator app.</p>
              <input data-testid="twofa-code" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verify()} placeholder="123456" maxLength={6} className="w-full mono text-center text-2xl tracking-[0.5em] px-3 py-3 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
              <button data-testid="twofa-verify" onClick={verify} disabled={loading} className="w-full mt-4 px-4 py-3 text-sm font-bold flex items-center justify-center gap-2 text-white disabled:opacity-60" style={{ background: "var(--primary)" }}>
                {loading ? <CircleNotch size={16} className="animate-spin" /> : null} Verify
              </button>
              <button onClick={() => { setTwoFA(null); setCode(""); }} className="w-full mt-3 text-sm hover:underline" style={{ color: "var(--text-2)" }}>Back</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
